import { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [requestTypes, setRequestTypes] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", request_type: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/contact-request-types").then((r) => setRequestTypes(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please complete required fields");
    if (!form.request_type) return toast.error("Please select what your request is about");
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch, inshaAllah.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", request_type: "" });
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Something went wrong"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative py-28 md:py-36 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0 pattern-girih opacity-25" />
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">Say Salam</div>
          <h1 className="display text-5xl md:text-7xl mt-4 text-ink">Get in touch</h1>
          <p className="mt-6 text-lg text-ink/65 font-light max-w-2xl mx-auto">
            Questions, visits, marriage services, facility rentals — we're here to help.
          </p>
        </div>
      </section>

      <section className="pb-24 px-6 md:px-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-10">
          <aside className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl bg-white border border-sand p-8">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Visit us</div>
              <div className="mt-3 flex gap-3 items-start">
                <MapPin size={18} className="text-emerald_deep mt-1 shrink-0" />
                <div className="text-ink">
                  <div className="font-medium">{settings?.address_line1 || "Dyewood Center"}</div>
                  <div className="text-sm text-ink/70">{settings?.address_line2 || "Flint, MI"}</div>
                </div>
              </div>
            </div>
            <div className="rounded-3xl bg-white border border-sand p-8">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Call</div>
              <a href={`tel:${settings?.phone || ""}`} className="mt-3 flex gap-3 items-center text-ink hover:text-emerald_deep">
                <Phone size={18} className="text-emerald_deep" /> <span className="font-medium">{settings?.phone || "(810) 720-1111"}</span>
              </a>
            </div>
            <div className="rounded-3xl bg-white border border-sand p-8">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Email</div>
              <a href={`mailto:${settings?.email || ""}`} className="mt-3 flex gap-3 items-center text-ink hover:text-emerald_deep">
                <Mail size={18} className="text-emerald_deep" /> <span className="font-medium break-all">{settings?.email || "info@flintislamiccenter.com"}</span>
              </a>
            </div>
            <div className="rounded-3xl overflow-hidden border border-sand aspect-[4/3]">
              <iframe
                title="FIC location"
                src="https://www.google.com/maps?q=Flint+Islamic+Center+Flint+MI&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </aside>
          <form onSubmit={submit} className="lg:col-span-3 rounded-3xl bg-white border border-sand p-8 md:p-10 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Your name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="contact-name" />
              <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="contact-email" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contact-phone" />
              <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="contact-subject" />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">What is your request about? *</label>
              <select
                value={form.request_type}
                onChange={(e) => setForm({ ...form, request_type: e.target.value })}
                required
                data-testid="contact-request-type"
                className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-emerald_deep bg-white"
              >
                <option value="">— Select one —</option>
                {requestTypes.map((t) => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
              </select>
              {requestTypes.find((t) => t.name === form.request_type)?.description && (
                <div className="mt-1 text-xs text-ink/55">{requestTypes.find((t) => t.name === form.request_type).description}</div>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Message *</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={6}
                required
                data-testid="contact-message"
                className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-emerald_deep resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 rounded-full bg-emerald_deep text-cream px-8 py-3.5 text-sm font-semibold uppercase tracking-wider hover:bg-emerald_deep-900 transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 size={14} className="animate-spin" />} Send message
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={label.endsWith("*")}
        data-testid={testId}
        className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-emerald_deep"
      />
    </div>
  );
}
